import { useNavigate } from 'react-router-dom'

import { PaginatedResponse, RecordType } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

// We use DataCollection here to check if any cost centers exist before rendering
// the employee assignments list. This ensures consistent styling and automatic
// updates when DataCollection styles change. The empty state redirects users to
// create their first cost center, while the main collection displays employees
// with their cost center assignments (current and future).
export const EmptyState = ({ hasConfigurationPending }: { hasConfigurationPending: boolean }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const noDataEmoji = useUnsafeMemo(() => {
    if (hasConfigurationPending) return '✍️'
  }, [hasConfigurationPending])

  const noDataTitle = useUnsafeMemo(() => {
    if (hasConfigurationPending)
      return i18n.t('finance.settings.cost_centers.assignments_empty_state.busy.title')

    return i18n.t('finance.settings.cost_centers.assignments_empty_state.title')
  }, [hasConfigurationPending])

  const noDataDescription = useUnsafeMemo(() => {
    if (hasConfigurationPending)
      return i18n.t('finance.settings.cost_centers.assignments_empty_state.busy.description')

    return i18n.t('finance.settings.cost_centers.assignments_empty_state.description')
  }, [hasConfigurationPending])

  const noDataActions = useUnsafeMemo(() => {
    const actions = []

    if (!hasConfigurationPending) {
      actions.push({
        label: i18n.t('finance.settings.cost_centers.assignments_empty_state.cta'),
        variant: 'outline' as const,
        icon: ExternalLink,
        onClick: () => {
          navigate(navigation.settings.finance.costCenters.list.fullPath())
        },
      })
    }

    return actions
  }, [hasConfigurationPending])

  const source = useDataCollectionSource({
    dataAdapter: {
      fetchData: (): Promise<PaginatedResponse<RecordType>> => {
        return Promise.resolve({
          type: 'pages',
          records: [],
          total: 0,
          currentPage: 1,
          perPage: 10,
          pagesCount: 1,
        })
      },
      perPage: 10,
      paginationType: 'pages',
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[{ type: 'table', options: { columns: [] } }]}
      emptyStates={{
        'no-data': {
          emoji: noDataEmoji,
          title: noDataTitle,
          actions: noDataActions,
          description: noDataDescription,
        },
      }}
    />
  )
}
